/* eslint-disable react/no-unstable-nested-components */
import React, { Fragment, useRef } from 'react';
import { Link as GatsbyLink } from 'gatsby';
import PropTypes from 'prop-types';
import {
  chakra,
  Container,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Text,
  Tr,
} from '@chakra-ui/react';
import { PortableText } from '@portabletext/react';

import getLink from '@repo/utils/getLink';
import { generateSectionId } from '@repo/ui-components/dept/utils/generateSectionId';
import Link from '@repo/ui-components/tailwind/Link';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import useOnFirstView from '@repo/ui-components/hooks/useOnFirstView';
import RichText from '../../../molecules/RichText';
import { BlockBuilderImage } from '../../../../sanity/types';

const handleDataLayer = (event, id) => {
  if (typeof window !== 'undefined') {
    window?.dataLayer?.push({ ecommerce: null });
    window?.dataLayer?.push({
      event,
      ecommerce: {
        promotion_id: id,
      },
    });
  }
};
function TableRichText({ data, promotionId }) {
  const productRef = data.filter(p => p?._type === 'product')?.[0];
  const { tt } = useUiComponentsInternationalContext();

  if (typeof productRef !== 'undefined') {
    const variants = productRef?.store?.variants;
    const comparePrice = variants?.[0]?.store?.compareAtPrice || 0;
    const minPrice = productRef?.store?.priceRange?.minVariantPrice;
    const maxPrice = productRef?.store?.priceRange?.maxVariantPrice;

    if (variants?.length === 1 && comparePrice !== 0) {
      return (
        <>
          <chakra.legend
            tabIndex="0"
            h="1px"
            width="1px"
            position="absolute"
            sx={{
              clip: 'rect(0, 0, 0, 0)',
            }}
          >
            {tt(s => s.blocksDeptComponents.originalPriceLabel, {
              comparePrice,
              minPrice,
            })}
          </chakra.legend>
          <Text
            as="span"
            aria-hidden="true"
            display="block"
            fontSize="xs"
            textDecoration="line-through"
          >
            ${comparePrice}
          </Text>
          <Text as="span" aria-hidden="true">
            ${minPrice}
          </Text>
        </>
      );
    }

    if (minPrice < maxPrice) {
      return (
        <>
          <Text as="span" display="block" fontSize="xs">
            {tt(s => s.blocksDeptComponents.startingAtLabel)}
          </Text>
          <Text>${minPrice}</Text>
        </>
      );
    }
    return <Text>${minPrice}</Text>;
  }

  const components = {
    types: {
      blockContentImage: BlockBuilderImage,
      link: ({ value }) => {
        const caption = value?.caption ?? 'link';
        if (value?.linkType === 'internal') {
          return (
            <Link
              to={
                getLink(value?.linkType, value?.href, value?.internalLink) ||
                value
              }
              textDecor="underline"
              _hover={{ textDecor: 'none' }}
              as={GatsbyLink}
              onClick={() => handleDataLayer('select_promotion', promotionId)}
            >
              {caption}
            </Link>
          );
        }

        const { blank = true, href } = value ?? {};
        return (
          <Link
            className="underline"
            link={href}
            target={blank ? '_blank' : '_self'}
          >
            {caption}
          </Link>
        );
      },
    },
    marks: {
      em: ({ children }) => <em>{children}</em>,
      strong: ({ children }) => <b>{children}</b>,
      sup: ({ children }) => (
        <Text
          as="sup"
          sx={{
            verticalAlign: 'middle',
          }}
        >
          {children}
        </Text>
      ),
    },
    block: {
      span: ({ children }) => <Text>{children}</Text>,
    },
  };

  return <PortableText value={data} components={components} />;
}

export default function ComparisonChart({ block }) {
  const ref = useRef();

  const { footer, highlightedColumnNum, table, promotionId } = block;
  const tableHeader = table?.rows?.[0];
  const colLength = table?.num_cols;
  const firstTdWidth = 40;

  useOnFirstView({
    callback: () => handleDataLayer('view_promotion', promotionId),
    observerProps: { threshold: 0.5 },
    ready: !!block,
    ref,
  });

  return (
    <Container {...generateSectionId(block)} ref={ref}>
      <TableContainer mb="4" whiteSpace="wrap">
        <Table variant="comparisonTable">
          {table?.heading && (
            <TableCaption placement="top">{table?.heading}</TableCaption>
          )}

          {tableHeader && (
            <Thead>
              <Tr>
                {tableHeader?.cells.map(cell => (
                  <Th key={cell?._key}>
                    <TableRichText
                      data={cell?.text}
                      promotionId={promotionId}
                    />
                  </Th>
                ))}
              </Tr>
            </Thead>
          )}

          <Tbody>
            {table?.rows?.slice(1).map(row => {
              const firstCell = row?.cells?.[0];

              return (
                <Fragment key={row._key}>
                  <Tr
                    display={{ base: 'table-row', md: 'none' }}
                    sx={{
                      '&:nth-of-type(4n + 1)': {
                        backgroundColor: 'primary.50',
                        border: 'none',
                      },
                      '&:nth-of-type(4n + 1) td': {
                        border: 'none',
                      },
                    }}
                  >
                    <Td colSpan={colLength}>
                      <TableRichText
                        data={firstCell?.text}
                        promotionId={promotionId}
                      />
                    </Td>
                  </Tr>

                  <Tr key={row?._key}>
                    {row?.cells.map((cell, index) =>
                      index === 0 ? (
                        <Td
                          display={{ base: 'none', md: 'table-cell' }}
                          key={cell._key}
                          sx={{
                            '@media (min-width: 768px)': {
                              '&:first-child': {
                                display: 'table-cell',
                                paddingInline: '1rem',
                                textAlign: 'left',
                                width: `${firstTdWidth}%`,
                              },
                            },
                          }}
                        >
                          <TableRichText
                            data={cell.text}
                            promotionId={promotionId}
                          />
                        </Td>
                      ) : (
                        <Td
                          bg={
                            highlightedColumnNum === index + 1
                              ? 'sunny.500'
                              : 'inherit'
                          }
                          key={cell._key}
                          width={{
                            base: `${100 / colLength}%`,
                            md: `${(100 - firstTdWidth) / colLength}%`,
                          }}
                        >
                          <TableRichText
                            data={cell?.text}
                            promotionId={promotionId}
                          />
                        </Td>
                      )
                    )}
                  </Tr>
                </Fragment>
              );
            })}
          </Tbody>
        </Table>
        {!!footer?.length && (
          <Container
            py="4"
            px="0"
            w="full"
            maxW="1024px"
            sx={{
              columnCount: 2,
              marginInline: 'auto',
              ul: {
                listStyle: 'none',
                margin: 'unset',
                padding: 'unset',
              },
            }}
          >
            <RichText body={footer} />
          </Container>
        )}
      </TableContainer>
    </Container>
  );
}

TableRichText.defaultProps = {
  data: [{}],
};

ComparisonChart.defaultProps = {
  block: {
    footer: null,
    highlightedColumnNum: null,
    table: {
      heading: null,
    },
  },
};

TableRichText.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  promotionId: PropTypes.string.isRequired,
};

ComparisonChart.propTypes = {
  block: PropTypes.shape({
    promotionId: PropTypes.string.isRequired,
    footer: PropTypes.arrayOf(PropTypes.shape({})),
    highlightedColumnNum: PropTypes.number,
    table: PropTypes.shape({
      heading: PropTypes.string,
      rows: PropTypes.arrayOf(PropTypes.shape({})),
      num_cols: PropTypes.number.isRequired,
    }),
  }),
};
